import React from 'react';
import PropTypes from 'prop-types';

import {styled, ThemeProvider} from "@mui/material/styles";
import {Button, createTheme} from "@mui/material";

import {CustomButtonContext} from "./CustomButtonContext";
import ButtonLoader from "./components/ButtonLoader";

import {
    black100,
    black500,
    blue50,
    blue100,
    blue200,
    blue300,
    blue600,
    blueColor,
    green500,
    grey200,
    grey400,
    grey500,
    greyishBlue200,
    greyishBlue300,
    red50,
    red100,
    red200,
    red300,
    red500,
    red600,
    greyishBlue500,
    grey300,
    green600,
    green50,
    green200,
    green300
} from "assets/jss/main";

const StyledButton = styled(Button, {
    shouldForwardProp: (prop) => !['thicker', 'customVariant', 'customColor', 'loading', 'rounded', 'contentJustify'].includes(prop), // Empêche la transmission de ces props` au DOM
})(({ theme, customColor, customVariant, thicker, rounded, contentJustify, noBorder }) => ({
    backgroundColor: ['outlined', 'nude'].includes(customVariant) ? '#fff' : customVariant === 'transparent' ? 'none' : theme.palette[customColor].background,
    color: ['outlined', 'nude'].includes(customVariant) ? theme.palette[customColor].outlinedText : theme.palette[customColor].text,
    borderRadius: rounded ? '48px' : '8px',
    textTransform: 'none',
    padding: '12px 16px',
    borderColor: blueColor,
    lineHeight: 'normal',
    fontWeight: '600',
    fontSize: '14px',
    height: thicker ? '48px' : '40px',
    whiteSpace: 'nowrap',
    justifyContent: contentJustify,
    [theme.breakpoints.down('md')]: {
        maxWidth: '100%'
    },
    ...(customVariant === 'outlined' || customVariant === 'transparent') && !noBorder && {
        border: `1px solid ${theme.palette[customColor].outlinedBorder}`,
    },
    '&:hover': {
        backgroundColor: customVariant === 'nude' ? theme.palette[customColor].hoverNude : customVariant === 'outlined' ? '#fff' : theme.palette[customColor].hoverBackground,
        ...customVariant === 'outlined' && {
            borderColor: theme.palette[customColor].hoverOutlinedBorder,
        }
    },
    '&.Mui-disabled' : {
        backgroundColor: customVariant === 'nude' ? '#fff' : grey200,
        color:  black100,
        border:'unset'
    },
}))

const theme = createTheme({
    palette: {
        default: {
            background: grey400,
            hoverBackground: grey500,
            text: black500,
            outlinedText: black500,
            outlinedBorder: greyishBlue200,
            hoverOutlinedBorder: greyishBlue300,
        },
        blue: {
            background: blueColor,
            hoverBackground: blue600,
            text: '#fff',
            outlinedText: blueColor,
            outlinedBorder: blue200,
            hoverOutlinedBorder: blue300,
            hoverNude: blue100,
        },
        red: {
            background: red500,
            hoverBackground: red600,
            text: '#fff',
            outlinedText: red500,
            outlinedBorder: red200,
            hoverOutlinedBorder: red300,
            hoverNude: red50,
        },
        lightBlue: {
            background: blue50,
            hoverBackground: blue100,
            text: blueColor,
            outlinedText: blueColor,
            outlinedBorder: blue200,
            hoverOutlinedBorder: blue300,
            hoverNude: blue100,
        },
        lightRed: {
            background: red50,
            hoverBackground: red100,
            text: red500,
            outlinedText: red500,
            outlinedBorder: red200,
            hoverOutlinedBorder: red300,
            hoverNude: red50,
        },
        idle: {
            background: grey200,
            hoverBackground: grey300,
            text: greyishBlue500,
            outlinedText: greyishBlue500,
            outlinedBorder: grey200,
            hoverOutlinedBorder: grey200,
            hoverNude: grey200
        },
        green: {
            background: green500,
            hoverBackground: green600,
            text: '#fff',
            outlinedText: green500,
            outlinedBorder: green200,
            hoverOutlinedBorder: green300,
            hoverNude: green50,
        },
    },
});

CustomButton.propTypes = {
    onClick: PropTypes.func,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    fullWidth: PropTypes.bool,
    thicker: PropTypes.bool,
    helperText: PropTypes.string,
    showHelperText: PropTypes.bool,
    rounded: PropTypes.bool,
    noBorder: PropTypes.bool,
    variant: PropTypes.oneOf([
        'outlined',
        'contained',
        'nude',
        'transparent'
    ]),
    color: PropTypes.oneOf([
        'default',
        'blue',
        'red',
        'lightBlue',
        'lightRed',
        'idle',
        'green'
    ])
};

function CustomButton({
    onClick,
    loading = false,
    disabled = false,
    fullWidth = false,
    thicker = false,
    rounded = false,
    showHelperText,
    helperText,
    variant = 'contained',
    color = 'blue',
    contentJustify = "center",
    noBorder = false,
    ...props
}) {
    return (
        <ThemeProvider theme={theme}>
            <CustomButtonContext.Provider value={{
                disabled: disabled,
                buttonVariant: variant,
                color: color
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: fullWidth ? '100%': 'fit-content'
                }}>
                    <StyledButton
                        customVariant={variant}
                        disableElevation
                        disabled={disabled}
                        onClick={onClick}
                        loading={loading}
                        thicker={thicker}
                        rounded={rounded}
                        customColor={color}
                        contentJustify={contentJustify}
                        noBorder={noBorder}
                        {...props}
                    >
                        {props.children}
                        {/* if we use @mui/lab -> https://mui.com/material-ui/react-button/#loading-button*/}
                        {loading && (
                            <ButtonLoader />
                        )}
                    </StyledButton>
                    {showHelperText && (
                        <span style={{
                            color: green500,
                            fontSize: '12px'
                        }}>
                            {helperText}
                        </span>
                    )}
                </div>
            </CustomButtonContext.Provider>
        </ThemeProvider>
    );
}

export default CustomButton;
